/* eslint-disable camelcase */
/* eslint-disable no-plusplus */
import {
  $deleteTableColumn,
  $getElementGridForTableNode,
  $getTableColumnIndexFromTableCellNode,
  $getTableNodeFromLexicalNodeOrThrow,
  $getTableRowIndexFromTableCellNode,
  $insertTableColumn,
  $insertTableRow,
  $removeTableRowAtIndex,
  HTMLTableElementWithWithTableSelectionState,
  TableCellNode,
  getTableSelectionFromTableElement,
} from '@lexical/table';
import { Divider, MenuItem } from '@mui/material';
import {
  $getRoot,
  $getSelection,
  DEPRECATED_$isGridSelection,
  LexicalEditor,
} from 'lexical';
import React from 'react';
import { useTranslation } from 'react-i18next';
import MergeCellMenuItem from '../MergeCellMenuItem';
import ToggleTableIsHeader from '../ToggleTableHeader';
import StyledMenu from './styles';

interface TableMenuActionsProps {
  activeEditor: LexicalEditor
  current: {
    node: TableCellNode;
    anchorEl: HTMLElement;
  }
  openMenu: boolean;
  onClose: () => void;
}
export default function TableActionsMenu(props: TableMenuActionsProps) {
  const {
    activeEditor, current, onClose, openMenu: open,
  } = props;
  const { t } = useTranslation('wysiwyg');
  const [tableCellNode, updateTableCellNode] = React.useState(current.node);
  const [selectionCounts, updateSelectionCounts] = React.useState({
    columns: 1,
    rows: 1,
  });

  React.useEffect(() => {
    activeEditor.getEditorState().read(() => {
      const selection = $getSelection();

      if (DEPRECATED_$isGridSelection(selection)) {
        const selectionShape = selection.getShape();

        updateSelectionCounts({
          columns: selectionShape.toX - selectionShape.fromX + 1,
          rows: selectionShape.toY - selectionShape.fromY + 1,
        });
      }
    });
  }, [activeEditor]);

  React.useEffect(() => activeEditor.registerMutationListener(TableCellNode, (nodeMutations) => {
    const nodeUpdated = nodeMutations.get(tableCellNode.getKey()) === 'updated';

    if (nodeUpdated) {
      activeEditor.getEditorState().read(() => {
        updateTableCellNode(tableCellNode.getLatest());
      });
    }
  }), [activeEditor, tableCellNode]);

  const clearTableSelection = React.useCallback(() => {
    activeEditor.update(() => {
      if (tableCellNode.isAttached()) {
        const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);
        const tableElement = activeEditor.getElementByKey(
          tableNode.getKey(),
        ) as HTMLTableElementWithWithTableSelectionState;

        if (!tableElement) {
          throw new Error('Expected to find tableElement in DOM');
        }

        const tableSelection = getTableSelectionFromTableElement(tableElement);
        if (tableSelection !== null) {
          tableSelection.clearHighlight();
        }

        tableNode.markDirty();
        updateTableCellNode(tableCellNode.getLatest());
      }

      const rootNode = $getRoot();
      rootNode.selectStart();
    });
  }, [activeEditor, tableCellNode]);

  const insertTableRowAtSelection = React.useCallback(
    (shouldInsertAfter: boolean) => {
      activeEditor.update(() => {
        if (!tableCellNode) return;
        const selection = $getSelection();

        const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);

        let tableRowIndex;

        if (DEPRECATED_$isGridSelection(selection)) {
          const selectionShape = selection.getShape();
          tableRowIndex = shouldInsertAfter
            ? selectionShape.toY
            : selectionShape.fromY;
        } else {
          tableRowIndex = $getTableRowIndexFromTableCellNode(tableCellNode);
        }

        const grid = $getElementGridForTableNode(activeEditor, tableNode);

        $insertTableRow(
          tableNode,
          tableRowIndex,
          shouldInsertAfter,
          selectionCounts.rows,
          grid,
        );

        clearTableSelection();

        onClose();
      });
    },
    [activeEditor, tableCellNode, selectionCounts.rows, clearTableSelection, onClose],
  );

  const insertTableColumnAtSelection = React.useCallback(
    (shouldInsertAfter: boolean) => {
      activeEditor.update(() => {
        const selection = $getSelection();

        const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);

        let tableColumnIndex;

        if (DEPRECATED_$isGridSelection(selection)) {
          const selectionShape = selection.getShape();
          tableColumnIndex = shouldInsertAfter
            ? selectionShape.toX
            : selectionShape.fromX;
        } else {
          tableColumnIndex = $getTableColumnIndexFromTableCellNode(
            tableCellNode,
          );
        }

        const grid = $getElementGridForTableNode(activeEditor, tableNode);

        $insertTableColumn(
          tableNode,
          tableColumnIndex,
          shouldInsertAfter,
          selectionCounts.columns,
          grid,
        );

        clearTableSelection();

        onClose();
      });
    },
    [
      activeEditor,
      tableCellNode,
      selectionCounts.columns,
      clearTableSelection,
      onClose,
    ],
  );

  const deleteTableRowAtSelection = React.useCallback(() => {
    activeEditor.update(() => {
      const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);
      const tableRowIndex = $getTableRowIndexFromTableCellNode(tableCellNode);

      $removeTableRowAtIndex(tableNode, tableRowIndex);

      clearTableSelection();
      onClose();
    });
  }, [activeEditor, tableCellNode, clearTableSelection, onClose]);

  const deleteTableAtSelection = React.useCallback(() => {
    activeEditor.update(() => {
      const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);
      tableNode.remove();

      clearTableSelection();
      onClose();
    });
  }, [activeEditor, tableCellNode, clearTableSelection, onClose]);

  const deleteTableColumnAtSelection = React.useCallback(() => {
    activeEditor.update(() => {
      const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);

      const tableColumnIndex = $getTableColumnIndexFromTableCellNode(
        tableCellNode,
      );

      $deleteTableColumn(tableNode, tableColumnIndex);

      clearTableSelection();
      onClose();
    });
  }, [activeEditor, tableCellNode, clearTableSelection, onClose]);

  return (
    <StyledMenu
      open={open}
      onClose={onClose}
      anchorEl={activeEditor.getElementByKey(tableCellNode.getKey())}
      keepMounted
      transformOrigin={{ vertical: -30, horizontal: 'right' }}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <MergeCellMenuItem editor={activeEditor} onClose={onClose} />
      <MenuItem onClick={() => { insertTableRowAtSelection(false); }}>
        {t('table.actions.insert-row-above', { count: selectionCounts.rows })}
      </MenuItem>
      <MenuItem onClick={() => { insertTableRowAtSelection(true); }}>
        {t('table.actions.insert-row-below', { count: selectionCounts.rows })}
      </MenuItem>
      <Divider />
      <MenuItem onClick={() => { insertTableColumnAtSelection(false); }}>
        {t('table.actions.insert-column-left', { count: selectionCounts.columns })}
      </MenuItem>
      <MenuItem onClick={() => { insertTableColumnAtSelection(true); }}>
        {t('table.actions.insert-column-right', { count: selectionCounts.columns })}
      </MenuItem>
      <Divider />
      <MenuItem onClick={() => { deleteTableColumnAtSelection(); }}>
        {t('table.actions.delete-column')}
      </MenuItem>
      <MenuItem onClick={() => { deleteTableRowAtSelection(); }}>
        {t('table.actions.delete-row')}
      </MenuItem>
      <MenuItem onClick={() => { deleteTableAtSelection(); }}>
        {t('table.actions.delete-table')}
      </MenuItem>
      <Divider />
      <ToggleTableIsHeader activeEditor={activeEditor} onClose={onClose} currentNode={tableCellNode} clearTableSelection={clearTableSelection} />
    </StyledMenu>
  );
}
